import { useState, useEffect, useRef } from "react";
import { comprimirImagen } from "@/shared/lib/imagenes";
import { ArrowLeft, Upload, Save, ImageIcon } from "lucide-react";
import SelectBuscable from '@/shared/components/SelectBuscable';
import { comoEventoDeCambio } from '@/shared/lib/opcionesDeSelect';
import { apiService } from '@/services/core/api.service';
import toast from "react-hot-toast";
import {
  RUTA_DE_RESPONSABLES,
  conElResponsableActual,
} from "@/features/almacen/lib/responsablesDeAlmacen";
import { estiloDeBoton } from '@/shared/lib/estilosDeBoton';

// ─── Styles ───────────────────────────────────────────────────────────────────

const READ_CLASS =
  "w-full px-3 py-2.5 rounded-lg text-sm text-gray-500 bg-[#F6F9FB]";

// ─── API types ────────────────────────────────────────────────────────────────

interface ToolOption {
  id: string;
  name: string;
  serialCode: string;
  currentMovementId: string | null;
  status: string;
  warehouseId: string | null;
}

interface UserOption {
  id: string;
  firstName: string;
  lastName: string;
}

interface ProjectOption {
  id: string;
  name: string;
}

interface WarehouseOption {
  id: string;
  name: string;
}

interface ToolDetailApi {
  name: string;
  serialCode: string;
  status: string;
  warehouseId: string;
  responsibleUserId: string | null;
  responsibleUserName: string | null;
  projectId: string | null;
  projectName: string | null;
  currentMovementId: string | null;
  maintenanceIntervalType: string | null;
}

// ─── Status mapping (display → API enum) ─────────────────────────────────────

const DISPLAY_TO_STATUS: Record<string, string> = {
  "En buen estado": "good",
  "Equipo nuevo": "new_equipment",
  "Falla/daño": "damaged",
  Faltante: "missing",
};

const STATUS_TO_DISPLAY: Record<string, string> = {
  good: "En buen estado",
  new_equipment: "Equipo nuevo",
  damaged: "Falla/daño",
  missing: "Faltante",
};

/**
 * Los estados con los que se puede entregar una herramienta (A-10).
 *
 * «Equipo nuevo» faltaba, así que una herramienta nueva se entregaba como «En
 * buen estado» y al reasignarla el select se quedaba en blanco, porque el
 * formulario lo precarga con el estado real y ese valor no estaba en la lista.
 */
const ESTADOS_DE_ENTREGA = [
  "En buen estado",
  "Equipo nuevo",
  "Falla/daño",
  "Faltante",
];

/** Al devolver no: una herramienta usada ya no vuelve como equipo nuevo. */
const ESTADOS_DE_DEVOLUCION = ["En buen estado", "Falla/daño", "Faltante"];

// ─── Payload type (exported for parent) ──────────────────────────────────────

export interface AsignacionFormPayload {
  /** tool id selected / from URL */
  herramienta: string;
  responsibleUserId: string;
  projectId?: string;
  resultingStatus?: string;
  warehouseId?: string;
  /** Horas de uso del ciclo — solo herramientas con intervalo por horas de uso */
  cycleUsageHours?: number;
  notes?: string;
  evidenceUrl1?: string;
  evidenceUrl2?: string;
  evidenceUrl3?: string;
}

// ─── Section card ─────────────────────────────────────────────────────────────

const SectionCard = ({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) => (
  <div
    className="bg-white rounded-xl border shadow-sm"
    style={{ borderColor: "#E0E0E0" }}
  >
    <div className="px-6 py-4 border-b" style={{ borderColor: "#E0E0E0" }}>
      <h2 className="text-base font-semibold text-gray-800">{title}</h2>
    </div>
    <div className="p-6">{children}</div>
  </div>
);

// ─── Photo card ───────────────────────────────────────────────────────────────

const PhotoCard = ({
  onUrlChange,
  onUploadingChange,
}: {
  onUrlChange?: (url: string) => void;
  onUploadingChange?: (uploading: boolean) => void;
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = "";
    // Mostrar preview local inmediatamente
    const localPreview = URL.createObjectURL(file);
    setPreview(localPreview);
    setUploading(true);
    onUploadingChange?.(true);
    try {
      const formData = new globalThis.FormData();
      formData.append("file", await comprimirImagen(file));
      const res = await apiService.request<{ url: string }>(
        "/almacen/upload/foto",
        {
          method: "POST",
          body: formData,
        },
      );
      URL.revokeObjectURL(localPreview);
      setPreview(res.url);
      onUrlChange?.(res.url);
    } catch {
      URL.revokeObjectURL(localPreview);
      setPreview(null);
      toast.error("Error al subir la foto. Intenta de nuevo.");
    } finally {
      setUploading(false);
      onUploadingChange?.(false);
    }
  };

  return (
    <div className="flex flex-col gap-2" style={{ width: 150 }}>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleFile}
      />
      <div
        className="relative rounded-xl border-2 border-dashed border-gray-300 flex items-center justify-center overflow-hidden bg-gray-50"
        style={{ width: 150, height: 150 }}
      >
        {preview ? (
          <img
            src={preview}
            alt="evidencia"
            className="w-full h-full object-contain"
          />
        ) : (
          <ImageIcon className="w-8 h-8 text-gray-300" />
        )}
        {uploading && (
          <div className="absolute inset-0 flex items-center justify-center bg-white/70">
            <div className="w-6 h-6 rounded-full border-2 border-[#315FB9] border-t-transparent animate-spin" />
          </div>
        )}
      </div>
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={uploading}
        className={estiloDeBoton("primario")}
      >
        <Upload className="w-4 h-4" />
        {uploading ? "Subiendo…" : "Cargar"}
      </button>
    </div>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────

export type AsignacionFormMode = "asignar" | "reasignar" | "devolver";

interface Props {
  mode?: AsignacionFormMode;
  toolId?: string;
  onBack: () => void;
  onSubmit?: (payload: AsignacionFormPayload) => Promise<void>;
}

interface FormState {
  herramienta: string;
  responsable: string;
  proyecto: string;
  estado: string;
  /** Horas de uso del ciclo, solo en devolucion de herramientas con intervalo por horas */
  horasUso: string;
  notes: string;
}

const TITLE_MAP: Record<AsignacionFormMode, { heading: string; sub: string }> =
  {
    asignar: {
      heading: "Asignación",
      sub: "Asigna una herramienta a un trabajador y proyecto",
    },
    reasignar: {
      heading: "Reasignar herramienta",
      sub: "Reasigna la herramienta a otro proyecto o responsable.",
    },
    devolver: {
      heading: "Devolución",
      sub: "Registra la devolución de una herramienta al almacén",
    },
  };

const AsignacionForm = ({
  mode = "asignar",
  toolId = "",
  onBack,
  onSubmit,
}: Props) => {
  const [form, setForm] = useState<FormState>({
    herramienta: toolId,
    responsable: "",
    proyecto: "",
    estado: "",
    horasUso: "",
    notes: "",
  });
  const [submitting, setSubmitting] = useState(false);

  // Evidence photos
  const [evidenceUrl1, setEvidenceUrl1] = useState("");
  const [evidenceUrl2, setEvidenceUrl2] = useState("");
  const [evidenceUrl3, setEvidenceUrl3] = useState("");
  const [photosUploading, setPhotosUploading] = useState(0);

  const handlePhotoUploading = (uploading: boolean) =>
    setPhotosUploading((prev) =>
      uploading ? prev + 1 : Math.max(0, prev - 1),
    );

  // Options from API
  const [toolOptions, setToolOptions] = useState<ToolOption[]>([]);
  const [userOptions, setUserOptions] = useState<UserOption[]>([]);
  /** Quien tiene la herramienta hoy, por si el catalogo filtrado no lo trae. */
  const [responsablePrevio, setResponsablePrevio] = useState<{
    id: string | null;
    nombre: string | null;
  }>({ id: null, nombre: null });
  const [projectOptions, setProjectOptions] = useState<ProjectOption[]>([]);
  const [warehouseOptions, setWarehouseOptions] = useState<WarehouseOption[]>(
    [],
  );
  const [currentToolName, setCurrentToolName] = useState<string>("");
  const [currentResponsableName, setCurrentResponsableName] =
    useState<string>("");
  const [currentProjectName, setCurrentProjectName] = useState<string>("");
  const [selectedWarehouseId, setSelectedWarehouseId] = useState<string>("");
  // Última herramienta elegida en el select, para descartar respuestas que
  // lleguen tarde cuando la selección ya cambió.
  const selectedToolRef = useRef<string>("");
  const [loadingOptions, setLoadingOptions] = useState(true);
  // En modo reasignar, si la herramienta no tiene asignación activa es en realidad una primera asignación
  const [isCurrentlyAssigned, setIsCurrentlyAssigned] = useState(true);
  const [toolStatus, setToolStatus] = useState<string>("");
  // Tipo de intervalo de mantenimiento de la herramienta: si es "usage_hours" hay
  // que capturar las horas del ciclo al devolverla (si no, nunca acumulan).
  const [toolIntervalType, setToolIntervalType] = useState<string>("");

  useEffect(() => {
    const load = async () => {
      try {
        const [toolsRes, usersRes, projectsRes, warehousesRes] =
          await Promise.all([
            apiService.request<{ data: ToolOption[]; total: number }>(
              "/almacen/herramientas?limit=100",
            ),
            // Solo quien tiene el modulo de Almacen: al resto no le llega la
            // asignacion a la app y nunca podria aprobarla (A-04).
            apiService.request<UserOption[]>(RUTA_DE_RESPONSABLES),
            apiService.request<{ data: ProjectOption[] } | ProjectOption[]>(
              "/projects/public/all",
            ),
            apiService.request<WarehouseOption[]>(
              "/almacen/almacenes/public/all",
            ),
          ]);

        // Available tools: no active movement, not in maintenance
        const rawTools: ToolOption[] = Array.isArray(toolsRes)
          ? (toolsRes as unknown as ToolOption[])
          : ((toolsRes as { data: ToolOption[] }).data ?? []);
        setToolOptions(
          rawTools.filter((h) =>
            mode === "devolver"
              ? // Para devolución: solo herramientas con asignación activa
                h.currentMovementId !== null
              : // Para asignación: solo disponibles (buen estado o equipo nuevo),
                // sin asignación activa. Excluye dañadas/faltantes/mala entrega,
                // en mantenimiento y dadas de baja.
                h.currentMovementId === null &&
                (h.status === "good" || h.status === "new_equipment"),
          ),
        );

        setUserOptions(Array.isArray(usersRes) ? usersRes : []);

        const proj = Array.isArray(projectsRes)
          ? projectsRes
          : ((projectsRes as { data: ProjectOption[] }).data ?? []);
        setProjectOptions(proj);

        setWarehouseOptions(Array.isArray(warehousesRes) ? warehousesRes : []);
      } catch {
        // fail silently — selects stay empty
      } finally {
        setLoadingOptions(false);
      }
    };
    load();

    // For reasignar/devolver: fetch tool details to pre-populate selects
    if (toolId && mode !== "asignar") {
      apiService
        .request<ToolDetailApi>(`/almacen/herramientas/${toolId}`)
        .then((t) => {
          setCurrentToolName(`${t.name} (${t.serialCode})`);
          setCurrentResponsableName(t.responsibleUserName ?? "Sin responsable");
          setCurrentProjectName(t.projectName ?? "Sin proyecto");
          setSelectedWarehouseId(t.warehouseId ?? "");
          setIsCurrentlyAssigned(t.currentMovementId !== null);
          setToolStatus(t.status ?? "");
          setToolIntervalType(t.maintenanceIntervalType ?? "");
          // Pre-populate responsable y proyecto con la asignacion actual.
          if (mode === "reasignar") {
            // Hay responsables de antes de A-04 que no tienen el modulo, asi
            // que el catalogo filtrado no los trae. Se guarda aparte y se
            // agrega al pintar: las dos cargas corren en paralelo y el detalle
            // de la herramienta suele ganarle al catalogo, asi que meterlo aqui
            // en la lista lo borraria la respuesta que llega despues.
            setResponsablePrevio({
              id: t.responsibleUserId,
              nombre: t.responsibleUserName,
            });
            setForm((prev) => ({
              ...prev,
              responsable: t.responsibleUserId ?? "",
              proyecto: t.projectId ?? "",
              estado: STATUS_TO_DISPLAY[t.status] ?? "",
            }));
          }
        })
        .catch(() => setCurrentToolName(toolId));
    }
  }, [toolId, mode]);

  /**
   * El catalogo con el que se pinta el select de responsable.
   *
   * Sale filtrado del servidor, mas quien ya tiene la herramienta si el filtro
   * lo dejo fuera: asi la pantalla dice quien la tiene hoy. En cuanto se elige
   * a otro deja de aparecer, que es lo que pide el ticket — sin el modulo no
   * puede aprobar en la app y no deberia poder recibirla de nuevo.
   */
  const opcionesDeResponsable = conElResponsableActual(
    userOptions,
    // Solo mientras siga siendo el elegido: en cuanto se pica a otro sale de
    // la lista y ya no se puede volver a el, que es el punto del filtro.
    form.responsable === responsablePrevio.id
      ? responsablePrevio
      : { id: null, nombre: null },
  );

  const set =
    (field: keyof FormState) =>
    (e: React.ChangeEvent<HTMLSelectElement | HTMLTextAreaElement>) => {
      const value = e.target.value;
      setForm((prev) => ({ ...prev, [field]: value }));
      // Al elegir herramienta guardamos su almacén; en devolución cargamos el
      // detalle para mostrar el responsable y proyecto actuales de la asignación.
      if (field === "herramienta") {
        const tool = toolOptions.find((t) => t.id === value);
        setSelectedWarehouseId(tool?.warehouseId ?? "");
        // Se entrega en el estado que tiene, y de ahí se puede cambiar (A-10).
        // Antes arrancaba vacío y el back guardaba «En buen estado» por su
        // cuenta, sin importar que la herramienta fuera nueva.
        if (mode === "asignar") {
          setForm((prev) => ({
            ...prev,
            estado: tool ? (STATUS_TO_DISPLAY[tool.status] ?? "") : "",
          }));
        }
        if (mode === "devolver") {
          // La selección puede cambiar mientras el detalle viaja: sin esta marca,
          // deseleccionar antes de que responda vuelve a pintar los datos de la
          // herramienta anterior justo después de haberlos limpiado.
          selectedToolRef.current = value;
          if (value) {
            apiService
              .request<ToolDetailApi>(`/almacen/herramientas/${value}`)
              .then((t) => {
                if (selectedToolRef.current !== value) return;
                setCurrentResponsableName(
                  t.responsibleUserName ?? "Sin responsable",
                );
                setCurrentProjectName(t.projectName ?? "Sin proyecto");
                setSelectedWarehouseId(t.warehouseId ?? "");
                setToolStatus(t.status ?? "");
                setToolIntervalType(t.maintenanceIntervalType ?? "");
              })
              .catch(() => {});
          } else {
            // Deseleccionar la herramienta (vuelve a "Seleccionar") debe
            // limpiar los datos auto-rellenados de la asignación anterior,
            // no dejarlos pegados de la selección previa.
            setCurrentResponsableName("");
            setCurrentProjectName("");
            setToolStatus("");
            setToolIntervalType("");
          }
        }
      }
    };

  // Validation
  const isValid =
    mode === "devolver"
      ? form.estado !== "" && (form.herramienta || toolId) !== ""
      : mode === "reasignar"
        ? form.responsable !== ""
        : form.herramienta !== "" && form.responsable !== "";

  const handleSubmit = async () => {
    if (!isValid || submitting) return;
    // Solo se reasigna una herramienta en condiciones de usarse. Mismo criterio
    // que para asignar: buen estado o equipo nuevo.
    if (
      mode === "reasignar" &&
      toolStatus &&
      toolStatus !== "good" &&
      toolStatus !== "new_equipment"
    ) {
      toast.error(
        "Solo se puede reasignar una herramienta en buen estado o equipo nuevo. Devuélvela primero.",
      );
      return;
    }
    setSubmitting(true);
    try {
      const payload: AsignacionFormPayload = {
        herramienta: form.herramienta || toolId,
        responsibleUserId: form.responsable,
        projectId: form.proyecto || undefined,
        resultingStatus: form.estado
          ? DISPLAY_TO_STATUS[form.estado]
          : undefined,
        warehouseId: selectedWarehouseId || undefined,
        cycleUsageHours:
          mode === "devolver" &&
          toolIntervalType === "usage_hours" &&
          form.horasUso !== ""
            ? Number(form.horasUso)
            : undefined,
        notes: form.notes.trim() || undefined,
        evidenceUrl1: evidenceUrl1 || undefined,
        evidenceUrl2: evidenceUrl2 || undefined,
        evidenceUrl3: evidenceUrl3 || undefined,
      };
      await onSubmit?.(payload);
      onBack();
    } catch (err: unknown) {
      const msg =
        typeof err === "object" && err !== null && "message" in err
          ? String((err as { message: unknown }).message)
          : "Error al guardar";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  // Si entramos por "reasignar" pero la herramienta no está asignada, es una primera asignación
  const effectiveMode: AsignacionFormMode =
    mode === "reasignar" && !isCurrentlyAssigned ? "asignar" : mode;
  const { heading, sub } = TITLE_MAP[effectiveMode];
  const showToolSelect = mode === "asignar" || (mode === "devolver" && !toolId);
  const showAssignFields = mode !== "devolver";

  return (
    <div className="mx-auto px-6 py-8">
      {/* Header */}
      <div className="flex items-start gap-3 mb-6">
        <button
          type="button"
          onClick={onBack}
          className={estiloDeBoton("secundario")}
        >
          <ArrowLeft className="w-4 h-4" />
          Volver
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{heading}</h1>
          <p className="text-sm text-gray-500 mt-0.5">{sub}</p>
        </div>
      </div>

      <div className="space-y-5">
        <SectionCard title="Información general">
          <div
            className={
              mode === "devolver"
                ? "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-4"
                : "grid grid-cols-2 gap-4"
            }
          >
            {/* Herramienta — select in asignar, read-only display in reasignar/devolver */}
            {showToolSelect ? (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Herramienta <span className="text-red-500">*</span>
                </label>
                <SelectBuscable
                  variante="formulario"
                  opciones={toolOptions.map((h) => ({ value: String(h.id), label: `${h.name} (${h.serialCode})` }))}
                  value={form.herramienta}
                  onChange={(v) => set("herramienta")(comoEventoDeCambio("herramienta", v))}
                  placeholder={loadingOptions
                    ? "Cargando..."
                    : toolOptions.length === 0
                      ? mode === "devolver"
                        ? "Sin herramientas asignadas"
                        : "Sin herramientas disponibles"
                      : "Seleccionar"}
                  disabled={loadingOptions}
                  placeholderDeBusqueda="Buscar herramienta..."
                />
              </div>
            ) : (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Herramienta
                </label>
                <p className={READ_CLASS}>
                  {currentToolName ||
                    (loadingOptions ? "Cargando..." : "Sin herramienta")}
                </p>
              </div>
            )}

            {/* Responsable — editable en asignar/reasignar, solo lectura en devolver */}
            {showAssignFields ? (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Responsable <span className="text-red-500">*</span>
                </label>
                <SelectBuscable
                  variante="formulario"
                  opciones={opcionesDeResponsable.map((u) => ({ value: String(u.id), label: `${u.firstName} ${u.lastName}` }))}
                  value={form.responsable}
                  onChange={(v) => set("responsable")(comoEventoDeCambio("responsable", v))}
                  placeholder={loadingOptions ? "Cargando..." : "Seleccionar"}
                  disabled={loadingOptions}
                  placeholderDeBusqueda="Buscar responsable..."
                />
              </div>
            ) : (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Responsable
                </label>
                <p className={READ_CLASS}>
                  {currentResponsableName ||
                    (loadingOptions ? "Cargando..." : "Sin responsable")}
                </p>
              </div>
            )}

            {/* Proyecto — editable en asignar/reasignar, solo lectura en devolver */}
            {showAssignFields ? (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Proyecto{" "}
                  <span className="text-gray-400 font-normal">(opcional)</span>
                </label>
                <SelectBuscable
                  variante="formulario"
                  opciones={projectOptions.map((p) => ({ value: String(p.id), label: p.name }))}
                  value={form.proyecto}
                  onChange={(v) => set("proyecto")(comoEventoDeCambio("proyecto", v))}
                  placeholder={loadingOptions ? "Cargando..." : "Seleccionar"}
                  disabled={loadingOptions}
                  placeholderDeBusqueda="Buscar proyecto..."
                />
              </div>
            ) : (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Proyecto asignado
                </label>
                <p className={READ_CLASS}>
                  {currentProjectName ||
                    (loadingOptions ? "Cargando..." : "Sin proyecto")}
                </p>
              </div>
            )}

            {/* Almacén — visible en reasignar */}
            {mode === "reasignar" && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Almacén
                </label>
                <SelectBuscable
                  variante="formulario"
                  opciones={warehouseOptions.map((w) => ({ value: String(w.id), label: w.name }))}
                  value={selectedWarehouseId}
                  onChange={setSelectedWarehouseId}
                  placeholder={loadingOptions ? "Cargando..." : "Seleccionar"}
                  disabled={loadingOptions}
                  placeholderDeBusqueda="Buscar almacén..."
                />
              </div>
            )}

            {/* Estado — en devolver es solo lectura (estado actual); editable en asignar/reasignar */}
            {mode === "devolver" ? (
              <div></div>
            ) : (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Estado
                </label>
                <SelectBuscable
                  variante="formulario"
                  opciones={ESTADOS_DE_ENTREGA.map((e) => ({ value: e, label: e }))}
                  value={form.estado}
                  onChange={(v) => set("estado")(comoEventoDeCambio("estado", v))}
                  placeholder="Seleccionar"
                  placeholderDeBusqueda="Buscar estado..."
                />
              </div>
            )}
          </div>
        </SectionCard>

        {/* Estado de devolución — solo en modo devolver */}
        {mode === "devolver" && (
          <SectionCard title="Estado de devolución">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Estado <span className="text-red-500">*</span>
                </label>
                <SelectBuscable
                  variante="formulario"
                  opciones={ESTADOS_DE_DEVOLUCION.map((e) => ({ value: e, label: e }))}
                  value={form.estado}
                  onChange={(v) => set("estado")(comoEventoDeCambio("estado", v))}
                  placeholder="Seleccionar"
                  placeholderDeBusqueda="Buscar estado..."
                />
              </div>
              {/* Horas de uso del ciclo: solo herramientas con intervalo por horas.
                  Sin esto las horas acumuladas nunca suben y la alerta por horas
                  no se dispara. */}
              {mode === "devolver" && toolIntervalType === "usage_hours" && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Horas de uso de este ciclo
                  </label>
                  <input
                    type="number"
                    min={0}
                    step={1}
                    value={form.horasUso}
                    onChange={(e) =>
                      setForm((prev) => ({ ...prev, horasUso: e.target.value }))
                    }
                    placeholder="Ej: 8"
                    className="w-full px-3 py-2.5 rounded-lg text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500 bg-[#F6F9FB]"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Se suman a las horas acumuladas para el mantenimiento.
                  </p>
                </div>
              )}

              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Descripción corta
                </label>
                <textarea
                  value={form.notes}
                  onChange={set("notes")}
                  rows={3}
                  maxLength={500}
                  placeholder="Describe brevemente el estado de la herramienta"
                  className="w-full px-3 py-2.5 rounded-lg text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500 bg-[#F6F9FB] resize-none"
                />
              </div>
            </div>
          </SectionCard>
        )}

        {/* Foto de evidencia */}
        <SectionCard title="Foto de evidencia">
          <div className="flex gap-4">
            <PhotoCard
              onUrlChange={setEvidenceUrl1}
              onUploadingChange={handlePhotoUploading}
            />
            <PhotoCard
              onUrlChange={setEvidenceUrl2}
              onUploadingChange={handlePhotoUploading}
            />
            <PhotoCard
              onUrlChange={setEvidenceUrl3}
              onUploadingChange={handlePhotoUploading}
            />
          </div>
        </SectionCard>
      </div>

      <div className="flex justify-end gap-3 mt-6">
        <button
          type="button"
          onClick={onBack}
          className={estiloDeBoton("secundario")}
        >
          Cancelar
        </button>
        <button
          type="button"
          onClick={handleSubmit}
          disabled={!isValid || submitting || photosUploading > 0}
          className={estiloDeBoton("primario")}
        >
          <Save className="w-4 h-4" />
          {submitting
            ? "Guardando…"
            : photosUploading > 0
              ? "Subiendo…"
              : "Guardar"}
        </button>
      </div>
    </div>
  );
};

export default AsignacionForm;
